var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import BaseTableContext, { useTableContext } from "./BaseTableContext";
import { SortableContainer, SortableHandle } from "react-sortable-hoc";
import BaseTable, { callOrReturn, Column, normalizeColumns } from "react-base-table";
import { MenuOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { chain, compact, isEmpty, isEqual, orderBy, uniq } from "lodash";
import Row from "./BaseTableComponents/Row";
import SelectionHeader from "./BaseTableComponents/SelectionHeader";
import SelectionCell from "./BaseTableComponents/SelectionCell";
import { TableFooter } from "./BaseTableComponents/TableFooter";
import "react-base-table/styles.css";
import "./BaseTable.scss";
import { Spin, Tooltip } from "antd";
import BaseTableContainer from "./BaseTableContainer";
import RegularFilter from "./BaseTableComponents/RegularFilter";
import BaseTableContextMenu from "./BaseTableComponents/ContextMenu";
import { checkIfTablehasFooter } from "./BaseTable.utils";
import BaseTableAutoResizer from "./BaseTableAutoResizer";
const DraggableContainer = SortableContainer(({ children }) => children);
const DraggableHandle = SortableHandle(({ children }) => children);
export const leftIconProps = {
    width: 48,
    flexShrink: 0,
    resizable: false,
    frozen: Column.FrozenDirection.LEFT,
    style: { justifyContent: "center" }
};
const BaseTableComponent = (props) => {
    const { onSelectedRowKeysChange, columns, data, children, draggable = true, selectable = false, columnsEditable = false, footerLeftActions = null, footerRightActions = null, tableId, disabled, className = "", triggers, hasIndex, readonly = false, useFilters = false, width, height, rowHeight, headerSummary, contextMenuItems, rowKey, loading = false, onColumnSortChange, deactivateComponentSortingForKeys, enablePagination, onPageOrPageSizeChange, countPerAllPages, disabledPagination, defaultPageSize, pageSizeOptions, minHeight, currentPage, disabledRowKeys, noOfSelectedRows } = props, rest = __rest(props, ["onSelectedRowKeysChange", "columns", "data", "children", "draggable", "selectable", "columnsEditable", "footerLeftActions", "footerRightActions", "tableId", "disabled", "className", "triggers", "hasIndex", "readonly", "useFilters", "width", "height", "rowHeight", "headerSummary", "contextMenuItems", "rowKey", "loading", "onColumnSortChange", "deactivateComponentSortingForKeys", "enablePagination", "onPageOrPageSizeChange", "countPerAllPages", "disabledPagination", "defaultPageSize", "pageSizeOptions", "minHeight", "currentPage", "disabledRowKeys", "noOfSelectedRows"]);
    const { getShownColumns, onSettingsChange } = useTableContext();
    const [localSelectedRowKeysPerAllPages, setLocalSelectedRowKeysPerAllPages] = useState([]);
    const [localSelectedRowKeysCurrentPage, setLocalSelectedRowKeysCurrentPage] = useState([]);
    const [contextMenuPosition, setContextMenuPosition] = useState();
    const [selectedContextMenuRow, setSelectedContextMenuRow] = useState();
    const [filteredValues, setFilteredValues] = useState();
    const [sortBy, setSortBy] = useState();
    const [selectedRowIndex, setSelectedRowIndex] = useState([]);
    const shownColumns = getShownColumns(tableId);
    const helperFunctionCalculateSelectedItemsOnCurrentPage = (itemsOnAllPages, filteredDataKeysParam) => enablePagination
        ? itemsOnAllPages.filter((r) => filteredDataKeysParam.includes(r))
        : itemsOnAllPages;
    const _contextMenuItems = useMemo(() => {
        return typeof contextMenuItems === "function"
            ? contextMenuItems(selectedContextMenuRow)
            : contextMenuItems;
    }, [contextMenuItems, selectedContextMenuRow]);
    const filteredColumns = useMemo(() => {
        if (isEmpty(shownColumns) || readonly)
            return columns;
        return columns.filter(({ key }) => shownColumns.includes(key));
    }, [columns.length, shownColumns, disabled, triggers]);
    const filteredData = useMemo(() => {
        if (!filteredValues)
            return data;
        const filterKeys = Object.keys(filteredValues).filter((key) => !isEmpty(filteredValues[key]));
        if (isEmpty(filterKeys))
            return data;
        return data.filter((item) => {
            return filterKeys.every((key) => {
                // Incase filterOn is set
                if (Array.isArray(item[key]))
                    return item[key].some((val) => {
                        return val && filteredValues[key].includes(val.value);
                    });
                return filteredValues[key].includes(item[key]);
            });
        });
    }, [data, filteredValues]);
    const filteredDataKeys = useMemo(() => {
        return filteredData.map((row) => row[rowKey]);
    }, [filteredData]);
    const selectableRowKeys = useMemo(() => {
        return filteredDataKeys.filter((dataKey) => !(disabledRowKeys === null || disabledRowKeys === void 0 ? void 0 : disabledRowKeys.includes(dataKey)));
    }, [filteredDataKeys]);
    const selectableRowKeysInAllPage = useMemo(() => {
        return localSelectedRowKeysPerAllPages.filter((rowId) => !(disabledRowKeys === null || disabledRowKeys === void 0 ? void 0 : disabledRowKeys.includes(rowId)));
    }, [localSelectedRowKeysPerAllPages, disabledRowKeys]);
    const selectableRowKeysInCurrentPage = useMemo(() => {
        return localSelectedRowKeysCurrentPage.filter((rowId) => !(disabledRowKeys === null || disabledRowKeys === void 0 ? void 0 : disabledRowKeys.includes(rowId)));
    }, [localSelectedRowKeysCurrentPage, disabledRowKeys]);
    const allSelected = useMemo(() => {
        if (!(selectableRowKeys === null || selectableRowKeys === void 0 ? void 0 : selectableRowKeys.length))
            return false;
        return selectableRowKeys.every((rowId) => selectableRowKeysInCurrentPage.includes(rowId));
    }, [selectableRowKeys, selectableRowKeysInCurrentPage]);
    const someButNotAllSelected = useMemo(() => {
        return (selectableRowKeys.some((rowId) => selectableRowKeysInCurrentPage.includes(rowId)) &&
            selectableRowKeys.some((rowId) => !selectableRowKeysInCurrentPage.includes(rowId)));
    }, [selectableRowKeys, selectableRowKeysInCurrentPage]);
    const sortedData = useMemo(() => {
        if (!(sortBy === null || sortBy === void 0 ? void 0 : sortBy.key) ||
            !(sortBy === null || sortBy === void 0 ? void 0 : sortBy.order) ||
            (deactivateComponentSortingForKeys === null || deactivateComponentSortingForKeys === void 0 ? void 0 : deactivateComponentSortingForKeys.includes(sortBy === null || sortBy === void 0 ? void 0 : sortBy.key))) {
            return filteredData;
        }
        return orderBy(filteredData, [sortBy === null || sortBy === void 0 ? void 0 : sortBy.key], [sortBy === null || sortBy === void 0 ? void 0 : sortBy.order]);
    }, [filteredData, sortBy]);
    const baseTable = createRef();
    const handlerRef = createRef();
    const tableWrapper = useRef(null);
    const getContainer = () => {
        var _a, _b;
        const container = (_a = baseTable.current) === null || _a === void 0 ? void 0 : _a.getDOMNode().querySelector(".BaseTable__table-frozen-left .BaseTable__body");
        return container || ((_b = baseTable.current) === null || _b === void 0 ? void 0 : _b.getDOMNode().parentNode);
    };
    const getHelperContainer = () => {
        var _a;
        return (_a = baseTable.current) === null || _a === void 0 ? void 0 : _a.getDOMNode().querySelector(".BaseTable__table-frozen-left");
    };
    const rowProps = (args) => {
        // don't forget to passing the incoming rowProps
        return {
            tagName: Row,
            index: args.rowIndex,
            totalElement: _columns.length
        };
    };
    const handleSortEnd = ({ oldIndex, newIndex }) => {
        const newData = [...data];
        const [removed] = newData.splice(oldIndex, 1);
        newData.splice(newIndex, 0, removed);
        if (typeof props.handleSortEnd === "function")
            props.handleSortEnd(oldIndex, newIndex);
    };
    const onRowsSelectionChange = (rowKeys) => {
        // filter out disabled rows
        const filteredRowKeys = rowKeys.filter((key) => !(disabledRowKeys === null || disabledRowKeys === void 0 ? void 0 : disabledRowKeys.includes(key)));
        setLocalSelectedRowKeysPerAllPages(rowKeys);
        const rowsKeysOnCurrentPage = helperFunctionCalculateSelectedItemsOnCurrentPage(filteredRowKeys, filteredDataKeys);
        setLocalSelectedRowKeysCurrentPage(rowsKeysOnCurrentPage);
    };
    const _handleSelectChange = ({ rowData }) => {
        const key = rowData[props.rowKey];
        const selected = selectableRowKeysInCurrentPage.includes(key);
        if (!selected) {
            onRowsSelectionChange([...selectableRowKeysInCurrentPage, key]);
        }
        else {
            onRowsSelectionChange(selectableRowKeysInCurrentPage.filter((rowKey) => rowKey !== key));
        }
    };
    const _handleSelectChangeAll = ({ selected }) => {
        if (selected) {
            const completeCurrentPagePlusSelectedItemsBefore = [
                ...localSelectedRowKeysPerAllPages,
                ...filteredDataKeys
            ];
            const uniqueSelection = [
                ...new Set(completeCurrentPagePlusSelectedItemsBefore)
            ];
            onRowsSelectionChange(uniqueSelection);
        }
        else {
            onRowsSelectionChange(localSelectedRowKeysPerAllPages.filter((rowKey) => !filteredDataKeys.includes(rowKey)));
        }
    };
    const _rowClassName = ({ rowData, rowIndex }) => {
        const { rowClassName, rowKey } = props;
        const selectedClassName = selectedContextMenuRow === (rowData === null || rowData === void 0 ? void 0 : rowData[rowKey])
            ? "row-selected__right-click"
            : "";
        const rowClass = rowClassName
            ? callOrReturn(rowClassName, { rowData, rowIndex })
            : "";
        const key = rowData[rowKey];
        return compact([
            rowClass,
            selectableRowKeysInAllPage.includes(key) && "row-selected",
            selectedClassName
        ]).join(" ");
    };
    const headerRenderer = ({ cells, columns }) => {
        return cells.map((cell, cellIdx) => {
            const col = columns[cellIdx];
            let cellReturn = Object.assign(Object.assign({}, cell), { props: Object.assign({}, cell.props) });
            if (useFilters) {
                const filterOn = col.filterOn || col.key;
                const filterOptions = chain(data.flatMap((item) => item[filterOn]))
                    .uniqBy((item) => {
                    if (typeof item === "object")
                        return item.value;
                    return item;
                })
                    .compact()
                    .value();
                if (!isEmpty(filterOptions)) {
                    cellReturn = Object.assign(Object.assign({}, cellReturn), { props: Object.assign(Object.assign({}, cellReturn.props), { children: [
                                ...cellReturn.props.children,
                                _jsx(RegularFilter, { options: filterOptions.map((opt) => {
                                        if (typeof opt === "object")
                                            return opt;
                                        return {
                                            value: opt,
                                            label: opt
                                        };
                                    }), value: filteredValues === null || filteredValues === void 0 ? void 0 : filteredValues[filterOn], onChange: (updatedValues) => {
                                        setFilteredValues(Object.assign(Object.assign({}, filteredValues), { [filterOn]: updatedValues }));
                                    } }, "filter")
                            ] }) });
                }
            }
            if (col === null || col === void 0 ? void 0 : col.headerTooltip)
                return _jsx(Tooltip, { title: col === null || col === void 0 ? void 0 : col.headerTooltip, children: cellReturn });
            return cellReturn;
        });
    };
    const onContextMenuSelect = ({ key }) => {
        const selectedItem = (_contextMenuItems || []).find((item) => item.key === key);
        if (selectedItem && typeof selectedItem.onSelect === "function") {
            const selectedItems = selectableRowKeysInAllPage.includes(selectedContextMenuRow || "")
                ? selectableRowKeysInAllPage
                : [selectedContextMenuRow];
            selectedItem.onSelect(selectedItems);
        }
    };
    const _columns = useMemo(() => {
        let results = filteredColumns || normalizeColumns(children || []);
        if (!disabled) {
            if (selectable) {
                const selectionColumn = Object.assign(Object.assign({}, leftIconProps), { key: "__selection__", cellRenderer: SelectionCell, headerRenderer: SelectionHeader, rowKey: "id", selectedRowKeys: localSelectedRowKeysPerAllPages, disabledRowKeys, onChange: _handleSelectChange, onChangeSelectAll: _handleSelectChangeAll, allSelected,
                    someButNotAllSelected });
                results = [selectionColumn, ...results];
            }
            if (draggable) {
                const draggableColumn = Object.assign(Object.assign({}, leftIconProps), { cellRenderer: (_jsx(DraggableHandle, { children: _jsx("span", { className: "te-cursor-pointer", children: _jsx(MenuOutlined, {}) }) })), key: "__draggable__" });
                results = [draggableColumn, ...results];
            }
        }
        if (hasIndex) {
            const indexColumn = Object.assign(Object.assign({ key: "colIndex", dataKey: "colIndex", title: "" }, leftIconProps), { cellRenderer: (cell) => {
                    return _jsx("span", { className: "text--primary", children: 1 + cell.rowIndex });
                } });
            results = [indexColumn, ...results];
        }
        return results;
    }, [
        filteredColumns,
        children,
        disabled,
        selectable,
        draggable,
        localSelectedRowKeysPerAllPages,
        data,
        triggers,
        filteredDataKeys,
        allSelected,
        someButNotAllSelected
    ]);
    const rowEventHandlers = {
        onContextMenu: ({ event, rowData, rowIndex }) => {
            setSelectedContextMenuRow(rowData === null || rowData === void 0 ? void 0 : rowData[rowKey]);
            setContextMenuPosition({
                left: event.pageX,
                top: event.pageY
            });
            if (!disabled) {
                event.preventDefault();
                document.addEventListener(`click`, function onClickOutside() {
                    // setDropDownState({ ...dropdownState, visible: false });
                    document.removeEventListener(`click`, onClickOutside);
                    setContextMenuPosition(undefined);
                    setSelectedContextMenuRow(undefined);
                });
                setSelectedRowIndex([...selectedRowIndex, rowIndex]);
                // setDropDownState({
                //   visible: true,
                //   coordinate: { x: event.clientX, y: event.clientY },
                //   rowIndex,
                //   activityId: rowData.id,
                //   parentId: rowData?.parentId,
                //   trackIndex: rowData?.trackIndex,
                // });
            }
        }
    };
    const scrollToItem = (evt) => {
        var _a;
        if (!baseTable.current)
            return;
        const itemIndex = (_a = evt.detail) === null || _a === void 0 ? void 0 : _a.itemIndex;
        if (itemIndex > -1) {
            baseTable.current.scrollToRow(itemIndex, "auto");
        }
    };
    // Init table settings
    useEffect(() => {
        onSettingsChange(tableId, {
            columns: columns.map(({ key }) => key)
        });
    }, [columns.length]);
    useEffect(() => {
        if (handlerRef.current && (baseTable === null || baseTable === void 0 ? void 0 : baseTable.current)) {
            handlerRef.current.addEventListener("scrollToItem", scrollToItem);
        }
    }, [baseTable === null || baseTable === void 0 ? void 0 : baseTable.current, handlerRef]);
    useEffect(() => {
        setLocalSelectedRowKeysCurrentPage(helperFunctionCalculateSelectedItemsOnCurrentPage(localSelectedRowKeysPerAllPages, filteredDataKeys));
    }, [filteredDataKeys]);
    useEffect(() => {
        if (typeof onSelectedRowKeysChange === "function") {
            onSelectedRowKeysChange(selectableRowKeysInAllPage);
        }
    }, [selectableRowKeysInAllPage]);
    useEffect(() => {
        // Clear all selection
        if (rest.selectedRowKeys === undefined) {
            setLocalSelectedRowKeysCurrentPage([]);
            setLocalSelectedRowKeysPerAllPages([]);
        }
        else if (rest.selectedRowKeys &&
            !isEqual(selectableRowKeysInCurrentPage, rest.selectedRowKeys)) {
            setLocalSelectedRowKeysCurrentPage(rest.selectedRowKeys);
            setLocalSelectedRowKeysPerAllPages(uniq([...localSelectedRowKeysPerAllPages, ...rest.selectedRowKeys]));
        }
    }, [rest.selectedRowKeys]);
    const tableFooter = useMemo(() => {
        if (!checkIfTablehasFooter(props))
            return null;
        return (_jsx(TableFooter, { tableId: tableId, current: currentPage, columns: columns.map(({ key, title }) => ({
                value: key,
                label: title
            })), footerLeftActions: footerLeftActions, footerRightActions: footerRightActions, columnsEditable: columnsEditable, hasFilters: useFilters, isReview: readonly, onClearFilters: () => setFilteredValues({}), enablePagination: enablePagination, onPageOrPageSizeChange: onPageOrPageSizeChange, countPerAllPages: countPerAllPages, defaultPageSize: defaultPageSize, disabledPagination: disabledPagination, pageSizeOptions: pageSizeOptions, noOfSelectedOnCurrentPage: noOfSelectedRows === undefined
                ? selectableRowKeysInCurrentPage.length
                : noOfSelectedRows, selectable: selectable }));
    }, [
        footerLeftActions,
        footerRightActions,
        columnsEditable,
        useFilters,
        readonly,
        enablePagination,
        countPerAllPages,
        defaultPageSize,
        disabledPagination,
        pageSizeOptions,
        currentPage
    ]);
    const _rowHeight = useMemo(() => rowHeight || 40, [rowHeight]);
    const tableHeight = useMemo(() => {
        let _height = data.length ? data.length * _rowHeight + 40 + 20 : 9999;
        if (tableFooter)
            _height += 40;
        return _height;
    }, [data, tableFooter]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: handlerRef, id: tableId || "" }), headerSummary && (_jsx("div", { className: "BaseTable__header BaseTable__header-summary te-flex te-justify-between te-items-center", children: headerSummary })), _jsx("div", { ref: tableWrapper, className: "te-flex te-flex-1 te-h-full te-w-full te-relative te-overflow-hidden", style: { minHeight }, children: _jsx(BaseTableAutoResizer, { ref: tableWrapper, width: width, height: height, loading: loading, children: ({ width, height }) => {
                        return (_jsx(DraggableContainer, { useDragHandle: true, getContainer: getContainer, helperContainer: getHelperContainer, onSortEnd: handleSortEnd, children: _jsx(BaseTable, Object.assign({ overlayRenderer: () => {
                                    if (!loading)
                                        return null;
                                    return (_jsx("div", { children: _jsx(Spin, {}) }));
                                }, fixed: true, ref: baseTable, width: width, height: Math.min(height, tableHeight, minHeight || 999999), className: classNames(className, {
                                    "BaseTable__table--disabled": disabled
                                }) }, rest, { columns: _columns, data: sortedData, rowProps: rowProps, rowClassName: _rowClassName, headerRenderer: headerRenderer, rowHeight: _rowHeight, headerHeight: 40, footerHeight: tableFooter ? 40 : 0, footerRenderer: tableFooter, rowEventHandlers: Object.assign(Object.assign({}, (rest.rowEventHandlers || {})), rowEventHandlers), sortBy: sortBy, ignoreFunctionInColumnCompare: false, useIsScrolling: true, onColumnSort: ({ order, key }) => {
                                    if (loading)
                                        return;
                                    if (typeof onColumnSortChange === "function") {
                                        onColumnSortChange(order, key);
                                    }
                                    if (!key)
                                        return;
                                    if ((sortBy === null || sortBy === void 0 ? void 0 : sortBy.key) && order === "asc") {
                                        setSortBy(undefined);
                                    }
                                    else {
                                        setSortBy({
                                            key,
                                            order
                                        });
                                    }
                                } })) }));
                    } }) }), _jsx(BaseTableContextMenu, { visible: !isEmpty(contextMenuPosition), contextMenuItems: _contextMenuItems || [], left: contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.left, top: contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.top, onContextMenuSelect: onContextMenuSelect })] }));
};
BaseTableComponent.Context = BaseTableContext;
const TEBaseTable = {
    Container: BaseTableContainer,
    Context: BaseTableContext,
    Component: BaseTableComponent
};
export default TEBaseTable;
